import { Router, Event as NavigationEvent, NavigationEnd } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { take, filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { ITab } from '@uxdf/direct-shared-lib';

import { LayoutConfigService } from '../../../../modules/config-layout/services/config.service';
import { State } from '../../../../redux/reducers';
import { Reset as ResetReport } from '../../../../redux/actions/invoices/actions-segments-report';
import { Update as UpdateConfig } from '../../../../redux/actions/invoices/actions-segments-config';
import { TitleBarService } from '../../../../redux/services/title-bar.service';

const tabs: { [key: string]: ITab } = {
  invoiceGeneralTabsDocuments: {
    title: $localize`:@@invoices-page.accountInvoicesTabDocuments:Documents`,
    link: '/account/invoices/documents'
  },
  invoiceGeneralTabsSegmentReports: {
    title: $localize`:@@invoices-page.accountInvoicesTabSegments:Segment reports`,
    link: '/account/invoices/segments'
  }
};

@Component({
  standalone: false,
  selector: 'app-invoices-page',
  templateUrl: './invoices-page.component.html',
  styleUrls: ['./invoices-page.component.scss']
})
export class InvoicesPageComponent implements OnDestroy, OnInit {
  tabs: ITab[];
  showTabs = false;
  subtitle: string;
  subscription: Subscription;

  constructor(
    private readonly layoutConfigService: LayoutConfigService,
    private readonly store: Store<State>,
    private readonly router: Router,
    private readonly titleBarService: TitleBarService
  ) {}

  ngOnInit(): void {
    const sourceSSO= localStorage.getItem('source') === 'ash' || localStorage.getItem('source') === 'snow';
    if(sourceSSO){
      this.titleBarService.setTitle($localize`:@@invoices-page.titleBarFirstLine:Invoices`);
    } 
    this.navigateToInvoices();

    this.subscription = this.router.events.subscribe((event: NavigationEvent) => {
      if (event instanceof NavigationEnd) {
        const pattern = new RegExp('/account/invoices');

        if (event.url.match(pattern)) {
          this.navigateToInvoices();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private navigateToInvoices() {
    this.layoutConfigService
      .getModuleLayout('invoice')
      .pipe(
        filter(layout => {
          const hasData = layout && layout.length > 0;
          return hasData;
        }),
        take(1)
      )
      .subscribe(
        (layout) => {
          const components = this.collectLayoutIds(layout);
          if (Object.keys(components).length === 0) {
            return;
          }
          this.updateLayout(components);
        },
        (error) => {
          console.error('[InvoicesPage] Error loading module layout:', error);
        }
      );

    this.store.dispatch(new ResetReport());
    this.store.dispatch(new UpdateConfig());
  }

  private collectLayoutIds(layout: any[]): { [id: string]: boolean } {
    const ids = {};
    for (const component of layout) {
      if (component.id) {
        ids[component.id] = true;
      }
      if (component.layout) {
        Object.assign(ids, this.collectLayoutIds(component.layout));
      }
    }
    return ids;
  }

  /**
   * Only tabs expected: either list or one tab as a whole page
   * Tab switch is managed by routing
   */
  private updateLayout(components: { [id: string]: boolean }) {
      this.showTabs = components['invoiceGeneralTabs'];
      this.tabs = Object.keys(components)
      .map(id => (this.showTabs ? tabs[id] : tabs[id.replace(/^invoice/, 'invoiceGeneralTabs')]))
      .filter(tabData => !!tabData);
      if (!this.tabs.length) {
        return;
      }
      this.tabs = this.tabs.map(tab => ({ ...tab, title: tab.title }));
      this.subtitle = this.showTabs ? '' : this.tabs[0].title;
      const activeTab = this.tabs.find(v => this.router.isActive(v.link, false)) || this.tabs[0];
      const activeRouterUrl = this.router.routerState.snapshot.url;
      const exactMatch = activeRouterUrl === activeTab.link;
      const subLinkMatch = activeTab.sublinksAllowd && activeRouterUrl.startsWith(activeTab.link);
      if (!(exactMatch || subLinkMatch)) {
        this.router.navigate([activeTab.link], { replaceUrl: true });
      }
  }
}
